import React, {useState, useEffect} from "react";
import { API_URL } from '../config/apiConfig';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Box,
    Alert,
    CircularProgress,
    DialogContentText,
    TextField,
    Collapse,
    IconButton
} from "@mui/material";
import { formatClassDate } from "../utils/classModalHelpers";
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";
import {getClassLeaderboard, deleteLeaderboardScore} from "../api/leaderboardApi";
import {getUserProfile} from "../api/profileApi";

export default function LeaderboardModal({open, onClose, classId, cls, hasScore, userProfile}) {
    const [leaderboard, setLeaderboard] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null);
    const [deletingScore, setDeletingScore] = useState(null);

    // Confirmation dialog states
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [addingRecord, setAddingRecord] = useState(false);
    const [error, setError] = useState("");
    const [userScore, setUserScore] = useState(null);

    const [comment, setComment] = useState(""); // New state for comment

    const [notificationOpen, setNotificationOpen] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationType, setNotificationType] = useState("success");

    // API_URL imported from config

    useEffect(() => {
        if (open && classId) {
            fetchLeaderboard();
        }
    }, [open, classId, userProfile]);

    const fetchLeaderboard = async () => {
        try {
            const response = await getClassLeaderboard(classId);
            setLeaderboard(response || []);

            // Find user's score by matching their full name
            if (userProfile && userProfile.fullName && response && response.length > 0) {
                const score = response.find(entry =>
                    entry.fullName === userProfile.fullName
                );

                setUserScore(score || null);
                setComment(score?.comment || "");
            }
        } catch (error) {
            console.error("❌ Error fetching leaderboard:", error);
            setLeaderboard([]);
        }
    };

    const handleFilterChange = (event, newFilter) => {
        if (newFilter) {
            setFilter(newFilter);
        }
    };

    const showNotification = (message, type = "info") => {
        setNotificationMessage(message);
        setNotificationType(type);
        setNotificationOpen(true);
    };

    const handleRowClick = (entryId) => {
        setExpandedRow(expandedRow === entryId ? null : entryId);
    };

    const handleDeleteScore = async (entryId) => {
        try {
            setDeletingScore(entryId);
            await deleteLeaderboardScore(entryId);

            // Refresh the leaderboard after successful deletion
            await fetchLeaderboard();

            // Close the expanded row
            setExpandedRow(null);

            showNotification("Score deleted successfully!", "success");
        } catch (error) {
            console.error("❌ Error deleting score:", error);
            showNotification("Failed to delete score. Please try again.", "error");
        } finally {
            setDeletingScore(null);
        }
    };

    // Show confirmation dialog
    const handleOpenConfirmation = () => {
        setError("");
        setComment(""); // Reset comment when opening dialog

        // Re-check if we have the user's score
        if (userProfile && userProfile.fullName && leaderboard && leaderboard.length > 0) {
            const score = leaderboard.find(entry => entry.fullName === userProfile.fullName);
            setUserScore(score || null);
            setComment(score?.comment || "");
            if (!score) {
                setError("You don't have a score for this class yet. Please add a score first.");
            }
        } else if (!userScore) {
            setError("You don't have a score for this class yet. Please add a score first.");
        }

        setConfirmDialogOpen(true);
    };

    // Handle closing the dialog
    const handleCloseConfirmation = () => {
        setConfirmDialogOpen(false);
        setError("");
        setComment("");
    };

    const handleConfirmAddRecord = async () => {
        try {
            // Double check that user has a score
            if (!userScore) {
                setError("You don't have a score for this class. Please add a score first.");
                return;
            }

            setAddingRecord(true);

            // Format the date from the class time
            const classDate = new Date(cls.time).toISOString().split('T')[0];

            // Create the record object with the user's existing score and comment
            const payload = {
                type: "WOD",
                name: cls.wodName ? cls.wodName.toUpperCase() : "WOD",
                date: classDate,
                score: userScore.score,
                comment: comment,
                scoreType: userScore.scoreType
            };

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/records`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const resData = await response.json();
                throw new Error(resData.error || 'Failed to create record');
            }

            setConfirmDialogOpen(false);
            showNotification("Successfully added to your records!", "success");
        } catch (err) {
            setError(err.message || "Error adding record");
        } finally {
            setAddingRecord(false);
        }
    };

    const filteredLeaderboard = (leaderboard || []).filter(entry =>
        filter === "all" || entry.scoreType?.toLowerCase() === filter
    );

    const role = localStorage.getItem('role');

    return (
        <Dialog open={open}
                onClose={onClose}
                maxWidth={false}
                fullWidth
                slotProps={{ root: { sx: { zIndex: 2100 } } }}
                PaperProps={{
                    sx: {
                        m: {xs: 0},
                        width: {xs: '100%'},
                        maxWidth: {xs: '100%'},
                        borderRadius: {xs: 0}
                    }
                }}>
            <DialogTitle>Leaderboard</DialogTitle>
            <DialogContent
            sx={{
                p: {xs: 0},
            }}
            >
                <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={handleFilterChange}
                    aria-label="Filter results"
                    sx={{mb: 2}}
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="rx">Rx</ToggleButton>
                    <ToggleButton value="sc">Sc</ToggleButton>
                    <ToggleButton value="beg">Beg</ToggleButton>
                </ToggleButtonGroup>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Place</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>
                                    Score
                                    <Typography
                                        component="span"
                                        color="text.secondary"
                                        sx={{ml: 1, fontSize: '0.9em'}}
                                    >
                                        (previous)
                                    </Typography>
                                </TableCell>
                                <TableCell>Type</TableCell>

                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLeaderboard.length > 0 ? (
                                filteredLeaderboard.map((entry, index) => (
                                    <React.Fragment key={entry.id}>
                                        <TableRow
                                            onClick={entry.fullName === userProfile?.fullName ?
                                                () => handleRowClick(entry.id) : undefined}
                                            sx={{
                                                cursor: entry.fullName === userProfile?.fullName ? 'pointer' : 'default',
                                                backgroundColor: entry.fullName === userProfile?.fullName ?
                                                    'rgba(25, 118, 210, 0.08)' : 'inherit',
                                                '&:hover': {
                                                    backgroundColor: entry.fullName === userProfile?.fullName ?
                                                        'rgba(25, 118, 210, 0.12)' : 'inherit'
                                                }
                                            }}
                                        >
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{entry.fullName}</TableCell>
                                            <TableCell>
                                                <Box>
                                                    <div>
                                                        <strong>{entry.score}</strong>
                                                        {entry.previousScore && (
                                                            <Typography
                                                                component="span"
                                                                color="text.secondary"
                                                                sx={{ml: 1, fontSize: '0.9em'}}
                                                            >
                                                                ({entry.previousScore})
                                                            </Typography>
                                                        )}
                                                    </div>
                                                    {entry.comment && (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                fontSize: '0.75em',
                                                                fontStyle: 'italic',
                                                                mt: 0.5,
                                                                lineHeight: 1.2
                                                            }}
                                                        >
                                                            {entry.comment}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{entry.scoreType?.toUpperCase()}</TableCell>

                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                                                {/* Only show collapse for current user's score */}
                                                {entry.fullName === userProfile?.fullName && (
                                                    <Collapse in={expandedRow === entry.id} timeout="auto" unmountOnExit>
                                                        <Box sx={{
                                                            margin: 1,
                                                            p: 2,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center'
                                                        }}>
                                                            <Button
                                                                variant="contained"
                                                                sx={{
                                                                    backgroundColor: '#d32f2f',
                                                                    color: 'white',
                                                                    '&:hover': {
                                                                        backgroundColor: '#b71c1c'
                                                                    },
                                                                    '&:disabled': {
                                                                        backgroundColor: '#ffcdd2',
                                                                        color: '#fff'
                                                                    }
                                                                }}
                                                                startIcon={deletingScore === entry.id ?
                                                                    <CircularProgress size={16} color="inherit" /> :
                                                                    <DeleteIcon />
                                                                }
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteScore(entry.id);
                                                                }}
                                                                disabled={deletingScore === entry.id}
                                                                size="small"
                                                            >
                                                                {deletingScore === entry.id ? 'Deleting...' : 'Delete Score'}
                                                            </Button>
                                                        </Box>
                                                    </Collapse>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No results available.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                {/* Only show Add to Records button for WOD type classes that have a wodName */}
                {cls && cls.trainingType === "WOD" && cls.wodName && role === "regular" && hasScore && (
                    <Button
                        onClick={handleOpenConfirmation}
                        variant="contained"
                        color="primary"
                    >
                        Add to Records
                    </Button>
                )}
                <Button onClick={onClose} variant="outlined">Close</Button>
            </DialogActions>

            {/* Confirmation Dialog with Comment Field */}
            <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmation} slotProps={{ root: { sx: { zIndex: 2200 } } }}>
                <DialogTitle>Add to Records</DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {error}
                        </Alert>
                    )}

                    {userScore ? (
                        <>
                            <DialogContentText>
                                Do you want to add this WOD result to your records?
                            </DialogContentText>

                            <Box sx={{mt: 2}}>
                                <Typography><strong>WOD:</strong> {cls?.wodName ? cls.wodName.toUpperCase() : "WOD"}
                                </Typography>
                                <Typography><strong>Date:</strong> {formatClassDate(cls?.time)}
                                </Typography>
                                <Typography><strong>Your Score:</strong> {userScore.score}</Typography>
                                <Typography><strong>Type:</strong> {userScore.scoreType?.toUpperCase()}</Typography>
                            </Box>

                            {/* New Comment Field */}
                            <TextField
                                label="Add Comment (Optional)"
                                multiline
                                rows={3}
                                fullWidth
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                margin="normal"
                                placeholder="Enter any notes about your performance..."
                                disabled={addingRecord}
                            />
                        </>
                    ) : (
                        <DialogContentText>
                            You haven't added a score for this class yet. Please add your score first before adding to
                            records.
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseConfirmation}
                        color="inherit"
                        disabled={addingRecord}
                    >
                        Cancel
                    </Button>
                    {userScore && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleConfirmAddRecord}
                            disabled={addingRecord || !userScore}
                        >
                            {addingRecord ? (
                                <>
                                    <CircularProgress size={20} sx={{mr: 1}}/>
                                    Saving...
                                </>
                            ) : (
                                "Confirm"
                            )}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Notification Dialog */}
            <Dialog
                open={notificationOpen}
                onClose={() => setNotificationOpen(false)}
                slotProps={{ root: { sx: { zIndex: 2200 } } }}
            >
                <DialogTitle>
                    {notificationType === "success" && "Success"}
                    {notificationType === "error" && "Error"}
                    {notificationType === "info" && "Information"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {notificationMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNotificationOpen(false)} variant="contained"
                            color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}